// component/policies/AdminIssuesPanel.js
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getToken } from "../../util/Cookies";
import config from "../../config";
import Navbar from "../../redesign/components/Navbar";
import Footer from "../../redesign/components/Footer";
import "./AdminIssuesPanel.css";


const STATUS_OPTIONS = ["open", "under_review", "resolved", "rejected"];
const STATUS_COLORS = {
  open: "#FF9500",
  under_review: "#26B8E3",
  resolved: "#34C759",
  rejected: "#FF3B30",
};


const AdminIssuesPanel = () => {
  const navigate = useNavigate();
  const { isLoggedIn, role } = useSelector((state) => state.auth);
  const [issues, setIssues] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUserType, setFilterUserType] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);


  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = new URLSearchParams({ page, limit: 20 });
      if (filterStatus) params.append("status", filterStatus);
      if (filterUserType) params.append("user_type", filterUserType);


      const res = await fetch(
        `${config.API_BASE_URL}/admin/issues?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setIssues(data.issues || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError("Failed to load issues");
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterUserType]);


  useEffect(() => {
    const isAdmin = isLoggedIn && (role || "").toLowerCase().includes("admin") ||
      (isLoggedIn && role === "provider");
    if (!isLoggedIn || !isAdmin) {
      navigate("/401");
      return;
    }
    fetchIssues();
  }, [fetchIssues, isLoggedIn, role, navigate]);


  const updateStatus = async (issueId, newStatus) => {
    setUpdatingId(issueId);
    try {
      const token = getToken();
      const res = await fetch(
        `${config.API_BASE_URL}/admin/issues/${issueId}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      if (res.ok) {
        setIssues((prev) =>
          prev.map((i) => (i.id === issueId ? { ...i, status: newStatus } : i))
        );
      }
    } catch (e) {
      alert("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };


  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };


  return (
    <div style={{ width: "100%" }}>
      <Navbar />
      <div className="admin-issues-container">
        <h1 className="admin-issues-title">Reported Issues Management</h1>


        {/* Filters */}
        <div className="admin-issues-filters">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ").toUpperCase()}</option>
            ))}
          </select>


          <select
            value={filterUserType}
            onChange={(e) => { setFilterUserType(e.target.value); setPage(1); }}
          >
            <option value="">All Users</option>
            <option value="user">Users</option>
            <option value="provider">Providers</option>
          </select>


          <span className="admin-issues-count">{total} issues found</span>
        </div>


        {/* Issues List */}
        {loading ? (
          <div className="admin-issues-loading">Loading...</div>
        ) : error ? (
          <div className="admin-issues-error">{error}</div>
        ) : issues.length === 0 ? (
          <div className="admin-issues-empty">No issues found</div>
        ) : (
          <div className="admin-issues-list">
            {issues.map((issue) => (
              <div
                key={issue.id}
                className={`admin-issue-card ${expandedId === issue.id ? "expanded" : ""}`}
                onClick={() => setExpandedId(expandedId === issue.id ? null : issue.id)}
              >
                <div className="admin-issue-header">
                  <div className="admin-issue-meta">
                    <span className="admin-issue-id">#{issue.id}</span>
                    <span
                      className="admin-issue-status"
                      style={{ backgroundColor: STATUS_COLORS[issue.status] + "22", color: STATUS_COLORS[issue.status] }}
                    >
                      {issue.status?.replace("_", " ")}
                    </span>
                    <span className="admin-issue-user-type">
                      {issue.userType || issue.user_type}
                    </span>
                  </div>
                  <div className="admin-issue-policy">
                    {issue.policyType?.policyType || issue.policyType?.policy_type || "-"}
                  </div>
                  <div className="admin-issue-date">{formatDate(issue.createdAt || issue.created_at)}</div>
                </div>


                {expandedId === issue.id && (
                  <div className="admin-issue-details">
                    <div className="admin-issue-description">
                      <strong>Description:</strong>
                      <p>{issue.description}</p>
                    </div>
                    <div className="admin-issue-info">
                      <span>Created By: #{issue.createdBy || issue.created_by}</span>
                      {issue.ipAddress && <span>IP: {issue.ipAddress || issue.ip_address}</span>}
                    </div>


                    {/* Status Update */}
                    <div className="admin-issue-actions">
                      <strong>Update Status:</strong>
                      <div className="admin-status-buttons">
                        {STATUS_OPTIONS.map((s) => (
                          <button
                            key={s}
                            disabled={issue.status === s || updatingId === issue.id}
                            className={`admin-status-btn ${issue.status === s ? "active" : ""}`}
                            style={{
                              borderColor: STATUS_COLORS[s],
                              color: issue.status === s ? "#fff" : STATUS_COLORS[s],
                              backgroundColor: issue.status === s ? STATUS_COLORS[s] : "transparent",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateStatus(issue.id, s);
                            }}
                          >
                            {updatingId === issue.id ? "..." : s.replace("_", " ")}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}


        {/* Pagination */}
        {total > 20 && (
          <div className="admin-issues-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>Page {page} of {Math.ceil(total / 20)}</span>
            <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};


export default AdminIssuesPanel;



